import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Contact } from "@models/common/contact";
import { IndividualDto } from "@models/individuals/individual-dto";
import {
  IndividualVerificationStatus,
  IndividualVerificationStatusLabels
} from '@models/individuals/individual-verification-status';
import { IndividualVerificationModel } from "@models/individuals/individual-verification.model";
import { IndividualVerificationProvider } from "@models/individuals/individuals-enums";
import { ConnectionStatusEnum } from "@models/integrations/connection-status.enum";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import {
  IContactsChangeEvent
} from "@pages/individuals/components/individuals-page/components/contacts/contacts.component";
import {
  BaseSignProvidersComponent
} from "@pages/settings/components/integrations-page/base-sign-providers/base-sign-providers.component";
import { getAnnatureConnectionItem } from "@pages/settings/components/integrations-page/constants/connection-items";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  ProviderConnectionStatus
} from "@shared/components/documents-related/sign-integrations-dropdown/model/provider-connection-status";
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { doNothing } from "@shared/utils/doNothing";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { catchError, defer, EMPTY, finalize, of, switchMap, tap } from "rxjs";
import {
  SelectVerificationContactModalComponent
} from "./components/select-verification-contact-modal/select-verification-contact-modal.component";
import { SendIndividualToVerificationModel } from "./models/send-to-verification.model";

@Component({
  selector: 'app-individual-id-verification',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbTooltip
  ],
  templateUrl: 'individual-id-verification.component.html',
  styleUrl: 'individual-id-verification.component.scss'
})
export class IndividualIdVerificationComponent extends BaseSignProvidersComponent implements OnInit {
  individualApiService = inject(IndividualsApiService);

  @Input({ required: true }) individualDto!: IndividualDto;
  @Input() currentProviderConnectionStatus: ProviderConnectionStatus | null = null;

  @Output() contactChanges = new EventEmitter<IContactsChangeEvent>();

  readonly IndividualVerificationStatusLabels = IndividualVerificationStatusLabels;
  readonly IndividualVerificationStatus = IndividualVerificationStatus;

  ngOnInit() {
    // Ensure verificationInfo is initialized
    if (!this.individualDto.verificationInfo) {
      this.individualDto.verificationInfo = new IndividualVerificationModel();
    }
  }

  sendOnVerification(): void {
    if (this.isESignProviderNotConnected) {
      this.showConnectAnnatureModalIfNotConnected();
      return;
    }

    const modalRef = this.modalService.open(SelectVerificationContactModalComponent, { size: 'lg' });
    const instance = modalRef.componentInstance as SelectVerificationContactModalComponent;

    instance.individualDto = this.individualDto;
    instance.contactsChangedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.contactChanges.emit(event);
      });

    instance.confirmVerification.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.isLoading.set(true)),

      switchMap((contact: Contact) => {
        const sendModel: SendIndividualToVerificationModel = {
          verificationProvider: IndividualVerificationProvider.Annature,
          contact
        };

        return this.individualApiService.triggerVerification(this.individualDto.individualId, sendModel)
          .pipe(
            tap((verificationModel) => {
                this.individualDto.verificationInfo = new IndividualVerificationModel(verificationModel);
                this.toastr.success(
                  'The verification request was submitted successfully. You will receive further details shortly at the provided contact information.'
                );
              }
            ),
            catchError((error: HttpErrorResponse) => {
              const errorMessage = ((error.error as { message: string }).message);
              if (errorMessage.includes('number does not adhere to E.164 format.')) {
                this.toastr.error('Failed to submit verification request. Incorrect phone number. Phone must be in E.164 format.');
              } else if (errorMessage.includes('organisation is not eligible to send verification')) {
                this.toastr.error('Failed to submit verification request. Your organisation is not eligible to send verification requests.');
              } else{
                this.toastr.error('Failed to submit verification request. Please try again later.');
              }

              return of(EMPTY);
            }),
            finalize(() => this.isLoading.set(false))
          );
      })
    ).subscribe();
  }

  voidVerification(): void {
    if (this.isESignProviderNotConnected) {
      this.showConnectAnnatureModalIfNotConnected();
      return;
    }

    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: defer(() =>
          this.individualApiService.cancelVerification(this.individualDto.individualId).pipe(
            tap((individualVerification) => {
              this.toastr.success('ID verification has been cancelled successfully.');
              this.individualDto.verificationInfo = individualVerification;
            }),
            catchError(() => {
              this.toastr.error('Failed to cancel ID verification. Please try again later.');
              return EMPTY;
            }),
            finalize(() => instance.close()),
            takeUntilDestroyed(this.destroyRef)
          )
        )
      },
      {
        text: 'Cancel',
        btnColor: 'gray',
        action: defer(() => {
          instance.close();
          return EMPTY;
        })
      }
    ];

    const body = `
      <div>You’re about to cancel ID verification. Any entered details will be lost.</div>
      <div class="mt-3">Do you wish to continue?</div>`;

    instance.config = {
      iconClass: 'ic-void-mail-20',
      header: 'Cancel ID verification',
      actions,
      body
    };
  }

  downloadCertificate() {
    const fileName = `ID_verification_certificate_${ this.individualDto.fullName }.pdf`;
    this.individualApiService
      .downloadSertificate(this.individualDto.individualId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error(`${ fileName } not found`, "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, fileName);
        }
      });
  }

  resetVerification(): void {
    if (this.isESignProviderNotConnected) {
      this.showConnectAnnatureModalIfNotConnected();
      return;
    }

    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: defer(() =>
          this.individualApiService.resetVerification(this.individualDto.individualId).pipe(
            tap((individualVerification) => {
              this.toastr.success('ID verification has been reset successfully.');
              this.individualDto.verificationInfo = individualVerification;
            }),
            catchError(() => {
              this.toastr.error('Failed to reset ID verification. Please try again later.');
              return EMPTY;
            }),
            finalize(() => instance.close()),
            takeUntilDestroyed(this.destroyRef)
          )
        )
      },
      {
        text: 'Cancel',
        btnColor: 'gray',
        action: defer(() => {
          instance.close();
          return EMPTY;
        })
      }
    ];

    const body = `
      <div>Resetting your verification will remove your current verified status. You will need to re-submit your documents and complete the verification process again.</div>
      <div class="mt-3">Do you want to proceed?</div>`;

    instance.config = {
      iconClass: 'ic-reset-20',
      header: 'Reset Verification',
      actions,
      body
    };
  }

  //TODO but not today
  // reviewDocuments(): void {
  //   const modalRef = this.modalService.open(VerificationReviewDocumentsComponent, { size: 'lg' });
  //   const instance = modalRef.componentInstance as VerificationReviewDocumentsComponent;
  // }

  initiateNewVerification(): void {
    this.sendOnVerification();
  }

  showConnectAnnatureModalIfNotConnected(): void {
    if (this.currentProviderConnectionStatus != null) return;

    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as CommonActionsModalComponent;

    const body = `
      To proceed with Director ID Verification,
      please connect your Annature account.
      Select "Connect" to sign in, or "Try for free"
      to create a new account.`;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Connect',
        action: of(null)
          .pipe(tap(() => this.connectToAnnature(getAnnatureConnectionItem())))
      },
      {
        text: 'Try for free',
        btnColor: 'white',
        action: of(null)
          .pipe(tap(() => this.registerAnnatureAccount(getAnnatureConnectionItem())))
      }
    ];

    componentInstance.config = {
      ...componentInstance.config,
      header: 'Connect Annature account',
      body,
      actions,
      showCloseBtn: true,
      cancelAction: of(null),
      closeBtnText: 'Cancel',
      closeBtnWrapperClass: 'flex-grow-1 d-flex justify-content-end'
    };

    modalRef.result.then(
      () => {},
      doNothing
    );
  }

  get isESignProviderNotConnected(): boolean {
    return this.currentProviderConnectionStatus == null
      || this.currentProviderConnectionStatus.status !== ConnectionStatusEnum.Connected;
  }
}
